import * as api from '@apis/app-user-contents';
import * as userApi from '@apis/app-user';
import { ReactComponent as DefaultAvatar } from '@assets/images/app/default-avatar-gray.svg';
import { ReactComponent as HeartOffGrayIcon } from '@assets/images/app/heart-gray.svg';
import { ReactComponent as HeartOnIcon } from '@assets/images/app/heart-pink.svg';
import { ReactComponent as ShareIcon } from '@assets/images/app/share.svg';
import { ReactComponent as HeaderDots } from '@assets/images/app/dots-horizontal.svg';
import ProductLinkButton from '@components/app/common/ProductLinkButton';
import { Linkify } from '@components/Linkify';
import { AppLoader } from '@components/Loader/AppLoader';
import { Roles } from '@consts/role';
import { useAppSocket } from '@hooks/appSocket';
import { useUserFavorites } from '@hooks/userFavorites';
import { useAppLoginModal } from '@stores/loginModalStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import { share } from '@utils/common';
import { cdnImageUrl } from '@utils/image';
import moment from 'moment';
import 'moment/locale/ko';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as AppLayout from '../AppLayout';
import { useAuthentication } from '@hooks/authentication';
import { useLoungeEditorModalStore } from '@stores/loungeEditorModalStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useAppNavigation } from '@hooks/appNavigation';
import { APP_BASE_PATH } from '@variables';
import { toast } from 'react-toastify';
import BottomActionSheet from '@components/app/common/BottomActionSheet';
import { DeepLinkActions, useDeeplink } from '@hooks/deeplink';

moment.locale('ko');

export const LoungeDetailPage: React.FC = () => {
  const params = useParams();
  const appSocket = useAppSocket();
  const loginModal = useAppLoginModal();
  const { authStore } = useAuthentication();
  const userContentId = params.userContentId;
  const myFavorite = useUserFavorites();
  const isFavorite = (userContentId && myFavorite.isFavoriteContent(userContentId)) || false;
  const [showBottomActionSheet, setShowBottomActionSheet] = useState<boolean>(false);
  const loungeEditorModal = useLoungeEditorModalStore();
  const dialog = useOkCancelDialog();
  const appNavigation = useAppNavigation();
  const [canUseButton, setCanUseButton] = useState<boolean>(true);
  const { linkTo } = useDeeplink();
  const userContentQuery = useQuery({
    queryKey: [`lounge-feed-item`, userContentId],
    queryFn: () => api.getUserContent(userContentId!),
    enabled: !!userContentId,
  });

  const removeUserContentMutation = useMutation({
    mutationFn: userApi.removeUserContent,
    onSuccess: async () => {
      setShowBottomActionSheet(false);
      appNavigation.to(`${APP_BASE_PATH}lounge`);
      setTimeout(() => {
        toast.dark('게시물이 삭제됐어요.', {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          pauseOnFocusLoss: false,
          draggable: true,
          progress: undefined,
          bodyStyle: {
            color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
            textAlign: 'center',
            fontFamily: 'Pretendard',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '20px',
          },
          theme: 'dark',
        });
      }, 500);
    },
    onError: () => {
      setShowBottomActionSheet(false);
      appNavigation.to(`${APP_BASE_PATH}lounge`);
      setTimeout(() => {
        toast.error('알 수 없는 오류가 발생했어요.', {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          pauseOnFocusLoss: false,
          draggable: true,
          progress: undefined,
          theme: 'light',
          style: {
            backgroundColor: '#FC4242',
            color: '#fff',
            textAlign: 'center',
          },
        });
      }, 500);
    },
  });

  useEffect(() => {
    document.title = '아이고고 | 라운지';
    return () => {
      document.title = '아이고고 - 아이를 위한 모든 경험';
    };
  }, []);

  useEffect(() => {
    if (!loungeEditorModal.show) {
      userContentQuery.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loungeEditorModal.show]);

  const userContent = userContentQuery.data?.data;
  const myContents = userContent?.publisherId === authStore?.user?.id;

  let title = '콘텐츠';
  if (userContent?.board === 'review') {
    title = '후기';
  } else if (userContent?.board === 'partner-news') {
    title = '선생님소식';
  }

  let suffix = '';
  if ((userContent?.publisher?.roles || []).includes(Roles.PARENT)) {
    suffix = ' 부모님';
  }
  if (userContent?.board === 'partner-news') {
    suffix = '';
  }

  const isPartner =
    userContent?.publisher?.roles?.includes(Roles.PARTNER) ||
    userContent?.publisher?.roles?.includes(Roles.TUTOR) ||
    userContent?.publisher?.roles?.includes(Roles.SELLER);

  let description = `${userContent?.publisher?.location} · ${userContent?.publisher?.age}`;
  if (userContent?.publisher?.roles?.includes(Roles.EDITOR) && userContent?.publisher?.message) {
    description = userContent?.publisher?.message;
  } else if (
    userContent?.publisher?.roles?.includes(Roles.TUTOR) ||
    userContent?.publisher?.roles?.includes(Roles.SELLER) ||
    userContent?.publisher?.roles?.includes(Roles.PARTNER)
  ) {
    description = `${userContent?.publisher?.categorySubList?.slice(0, 2).join(', ') || ''}`;
  }

  return (
    <AppLayout.Layout>
      <AppLayout.Header
        title={title}
        rightButton={
          userContent?.publisherId === authStore?.user?.id && (
            <div
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setShowBottomActionSheet(true);
              }}>
              <HeaderDots />
            </div>
          )
        }
        style={{ borderBottom: '1px solid #EFF1F4' }}></AppLayout.Header>
      <AppLayout.Contents>
        <OverlayScrollbarsComponent
          defer
          options={{
            scrollbars: { autoHide: 'scroll' },
            overflow: {
              x: 'hidden',
            },
          }}
          style={{
            backgroundColor: '#ffffff',
            marginTop: 0,
            position: 'relative',
            overflow: 'auto',
            height: 'calc(100% - 65px)',
            width: '100%',
          }}>
          {userContentQuery.isLoading && <AppLoader />}
          {userContentQuery.isSuccess && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px 0' }}>
              <div
                style={{
                  padding: '16px 20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  borderBottom: '1px solid #EFF1F4',
                }}>
                <div
                  style={{ flexShrink: 0, cursor: isPartner ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (isPartner) {
                      linkTo(
                        {
                          action: DeepLinkActions.GO_TO_PARTNER,
                          payload: { partnerId: userContent?.publisherId },
                        },
                        {
                          source: 'lounge-detail',
                        },
                      );
                    }
                  }}>
                  {userContent?.publisher?.imageUrl ? (
                    <img
                      src={cdnImageUrl(userContent?.publisher?.imageUrl)}
                      alt="avatar"
                      style={{ objectFit: 'cover', width: '40px', height: '40px', borderRadius: '20px' }}
                    />
                  ) : (
                    <DefaultAvatar />
                  )}
                </div>
                <div
                  style={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    cursor: isPartner ? 'pointer' : 'default',
                  }}
                  onClick={() => {
                    if (isPartner) {
                      linkTo(
                        {
                          action: DeepLinkActions.GO_TO_PARTNER,
                          payload: { partnerId: userContent?.publisherId },
                        },
                        {
                          source: 'lounge-detail',
                        },
                      );
                    }
                  }}>
                  <div
                    style={{
                      color: 'var(--Neutral-90, #424242)',
                      fontFamily: 'Pretendard',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: '20px',
                    }}>
                    {userContent?.publisher?.nickname + suffix}
                  </div>
                  <div
                    style={{
                      color: 'var(--Neutral-60, #9B9DA0)',
                      fontFamily: 'Pretendard',
                      fontSize: '13px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      lineHeight: '18px',
                      cursor: isPartner ? 'pointer' : 'default',
                    }}
                    onClick={() => {
                      if (isPartner) {
                        linkTo(
                          {
                            action: DeepLinkActions.GO_TO_PARTNER,
                            payload: { partnerId: userContent?.publisherId },
                          },
                          {
                            source: 'lounge-detail',
                          },
                        );
                      }
                    }}>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {description}
                    </div>
                    <div style={{ flexShrink: 0 }}>{moment(userContent?.publishedAt).fromNow()}</div>
                  </div>
                </div>
              </div>
              <div className="ProseMirror" style={{ padding: '0 20px' }}>
                <Linkify>{userContent?.text}</Linkify>
              </div>
              <div style={{ padding: '0 20px', display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                {userContent?.images &&
                  userContent?.images.length > 0 &&
                  userContent?.images
                    .filter((item) => {
                      return !userContent.text.includes(item);
                    })
                    .map((item, index) => {
                      const isMp4 = item.toLowerCase().endsWith('.mp4');
                      return isMp4 ? (
                        <video
                          key={`video-${index}`}
                          src={cdnImageUrl(item)}
                          style={{ display: 'block', width: '100%' }}
                          autoPlay
                          muted
                          loop
                          playsInline
                        />
                      ) : (
                        <img
                          key={`image-${index}`}
                          src={cdnImageUrl(item)}
                          alt={`upload-${index}`}
                          style={{ display: 'block' }}
                        />
                      );
                    })}
              </div>
              {userContent?.product?.title && (
                <ProductLinkButton product={userContent?.product} source={`user-content-detail`} />
              )}
            </div>
          )}
        </OverlayScrollbarsComponent>
        <div style={{ height: '64px', borderTop: '1px solid #EFF1F4' }}>
          <div
            style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              justifyContent: 'space-around',
              padding: '0 20px',
              height: '56px',
            }}>
            <button
              disabled={!canUseButton}
              className="link-button"
              style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                if (!authStore.user) {
                  loginModal.open();
                  return;
                }

                if (!canUseButton) {
                  return;
                }

                setCanUseButton(false);
                setTimeout(() => {
                  setCanUseButton(true);
                }, 3000);

                if (userContentId) {
                  if (isFavorite) {
                    myFavorite.removeContentFromFavorites(userContentId);
                    appSocket.sendEvent({
                      type: 'click',
                      data: {
                        userId: authStore.user?.id,
                        path: document.location.pathname,
                        actionData: {
                          from: document.location.pathname,
                          actionType: 'favorite-remove',
                          userContentId: userContentId,
                          id: 'user-content-favorite',
                        },
                      },
                    });
                  } else {
                    myFavorite.addContentToFavorites(userContentId);
                    appSocket.sendEvent({
                      type: 'click',
                      data: {
                        userId: authStore.user?.id,
                        path: document.location.pathname,
                        actionData: {
                          from: document.location.pathname,
                          actionType: 'favorite-add',
                          userContentId: userContentId,
                          id: 'user-content-favorite',
                        },
                      },
                    });
                  }
                }
              }}>
              {isFavorite ? <HeartOnIcon /> : <HeartOffGrayIcon />}
              {userContent?.like !== undefined && Number(userContent.like) > 0 && (
                <span
                  style={{
                    color: 'var(--Neutral-70, #808387)',
                    textAlign: 'right',
                    fontFamily: 'Pretendard',
                    fontSize: '16px',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    marginLeft: '6px',
                  }}>
                  {userContent?.like}
                </span>
              )}
            </button>
            <button
              className="link-button"
              style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                share({ type: 'user-content', title: userContent?.title || title, id: userContentId });
                appSocket.sendEvent({
                  type: 'click',
                  data: {
                    userId: authStore.user?.id,
                    path: document.location.pathname,
                    actionData: {
                      from: document.location.pathname,
                      actionType: 'share',
                      userContentId: userContentId,
                      id: 'user-content-share-from-detail',
                    },
                  },
                });
              }}>
              <ShareIcon />
            </button>
          </div>
        </div>
      </AppLayout.Contents>
      <BottomActionSheet
        open={showBottomActionSheet}
        onClose={() => setShowBottomActionSheet(false)}
        btnInfos={[
          {
            text: '수정하기',
            onClick: () => {
              const category = userContent?.category;
              if (category === 'review') {
                const isBestReview = !!userContent?.bestDate;
                const role = authStore?.user?.role;
                if (isBestReview && role === Roles.PARENT) {
                  dialog.open({
                    title: `베스트 후기는 수정하실 수 없습니다.`,
                    content: `수정이 필요한 경우 아이고고 고객센터로 문의주세요.`,
                    type: 'ok_cancel',
                  });
                  return;
                }

                loungeEditorModal.open({
                  userContentId: userContentId,
                  board: 'review',
                  paymentStatement: userContent?.paymentStatement,
                });
              } else {
                loungeEditorModal.open({ userContentId: userContentId, board: 'curation', category: category });
              }
              setShowBottomActionSheet(false);
            },
            isHide: !myContents,
          },
          {
            text: myContents ? '삭제하기' : '신고하기',
            color: '#FF3B30',
            onClick: () => {
              if (myContents) {
                const isBestReview = !!userContent?.bestDate;
                const role = authStore?.user?.role;

                if (userContent?.category === 'review') {
                  if (isBestReview && role === Roles.PARENT) {
                    dialog.open({
                      title: `베스트 후기는 삭제하실 수 없습니다.`,
                      content: `삭제가 필요한 경우 아이고고 고객센터로 문의주세요.`,
                      type: 'ok_cancel',
                    });
                    return;
                  }
                  dialog.open({
                    title: `후기를 삭제하시겠어요?`,
                    content: `후기를 삭제하면 재작성이 불가합니다.`,
                    type: 'ok_cancel',
                    onConfirm: () => {
                      if (userContentId) {
                        removeUserContentMutation.mutate(userContentId);
                      }
                    },
                  });
                } else {
                  if (userContent?.marketingAgreed) {
                    dialog.open({
                      title: `알림`,
                      content: `마케팅 활용에 동의한 콘텐츠는 삭제하실 수 없습니다.`,
                      type: 'ok',
                    });
                  } else {
                    dialog.open({
                      title: `삭제하시겠어요?`,
                      content: `삭제한 내용은 되돌릴 수 없어요.`,
                      type: 'ok_cancel',
                      confirmText: '삭제',
                      onConfirm: () => {
                        if (userContentId) {
                          removeUserContentMutation.mutate(userContentId);
                        }
                      },
                    });
                  }
                }
              } else {
                // TODO: 신고하기 기능 추가
              }
            },
          },
        ]}
      />
      <AppLayout.Footer hide></AppLayout.Footer>
    </AppLayout.Layout>
  );
};

export default LoungeDetailPage;
